/** Change owner… (plan §2.1 `owner`, §3) — the user picker of the change_owner action.
 *
 *  A booking moves only to a guest of its own tenant family (`switch_user` refuses a
 *  cross-tenant move), so the search is scoped to the guest's tenant — the parent operator
 *  for a franchise licensee's booking — and every option shows name, pk and tenant (the
 *  remote-autocomplete rule) with the email and phone that tell same-named guests apart.
 *  The console-wide `UserPicker` searches every tenant, so it is not used here.
 *
 *  The pick then queues in the draft (edit mode, or a draft kept in the editor) or saves
 *  at once (`… now`, clean draft) — the same two calls every action control makes. */
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Button, Typography } from '@mui/material';
import { useCallback, useState } from 'react';

import { ownerOptions, type BookingUserOption } from '@/api/bookings';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { useRecordActions } from '@/features/records/actions/RecordActionsProvider';
import { draftValuesEqual } from '@/features/records/draft/changeSet';
import { useDraft } from '@/features/records/draft/DraftProvider';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import type { RecordActionWidgetProps, RecordOptionRef } from '@/features/records/editor/types';
import { refMeta } from '@/features/records/fields/widgets/pickers';

/** Below this many characters the owner search does not run (the users table is large;
 *  the endpoint answers nothing either). */
const MIN_QUERY = 2;

export function ownerRef(o: BookingUserOption): RecordOptionRef {
  const name = [o.first_name, o.last_name].filter(Boolean).join(' ');
  return {
    id: o.id,
    label: name || o.email || o.phone || `#${o.id}`,
    tenant: o.tenant?.code ?? null,
    extra: [name ? o.email : null, o.phone],
  };
}

const refKey = (r: RecordOptionRef) => r.id;
const refLabel = (r: RecordOptionRef) => r.label;

export function createChangeOwnerWidget({ tenantId }: { tenantId: number }) {
  function ChangeOwnerWidget({ action, rec, anchorEl, onClose }: RecordActionWidgetProps) {
    const { config, fieldByKey, formatValue, rememberOptions } = useRecordEditor();
    const actions = useRecordActions();
    const { state } = useDraft();
    const [picked, setPicked] = useState<RecordOptionRef | null>(null);
    const field = fieldByKey.get(action.field);
    const current = actions.valueOf(action, rec);
    const modes = actions.modes(action);
    const queryFn = useCallback(
      async (q: string, signal: AbortSignal) =>
        q.trim().length < MIN_QUERY ? [] : (await ownerOptions(tenantId, q, signal)).map(ownerRef),
      [],
    );
    const verdict = field ? config.verdictOf(rec, field, state) : null;
    const caution = verdict && verdict.verdict !== 'kept' && verdict.evidence ? verdict.evidence : action.warning;
    const same = picked !== null && draftValuesEqual(picked.id, current);
    const ready = picked !== null && !same;

    const run = (now: boolean) => {
      if (!picked || !ready) return;
      rememberOptions(action.field, [picked]);
      const req = { action, rec, to: picked.id, label: `Change owner to ${picked.label}`, anchorEl };
      onClose();
      if (now) actions.runNow(req);
      else actions.queue(req);
    };

    return (
      <Box sx={{ display: 'grid', gap: 1 }} data-change-owner>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {action.label}
        </Typography>
        {action.help && (
          <Typography variant="caption" color="text.secondary">
            {action.help}
          </Typography>
        )}
        <Typography variant="caption" component="div">
          Now: <b>{field ? formatValue(field, rec, current) : String(current ?? '—')}</b>
          {typeof current === 'number' ? ` · #${current}` : ''}
        </Typography>
        <RemoteAutocomplete<RecordOptionRef>
          label="New owner"
          size="small"
          value={picked}
          queryFn={queryFn}
          getOptionKey={refKey}
          getOptionLabel={refLabel}
          getOptionMeta={refMeta}
          getOptionDisabled={(r) => draftValuesEqual(r.id, current)}
          placeholder="Name, email or phone"
          noOptionsText={`Type at least ${MIN_QUERY} characters of a guest of this tenant`}
          inputProps={{ autoFocus: true }}
          onChange={(v) => setPicked((v as RecordOptionRef | null) ?? null)}
        />
        {same && (
          <Typography variant="caption" color="text.secondary">
            That guest already owns this booking.
          </Typography>
        )}
        {caution && (
          <Typography variant="caption" component="div" sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
            <WarningAmberIcon aria-hidden sx={{ fontSize: 14, color: 'warning.main', mt: '2px', flex: 'none' }} />
            <span>{caution}</span>
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Button size="small" onClick={onClose}>
            Cancel
          </Button>
          {modes.now && (
            <Button size="small" variant={modes.queue ? 'outlined' : 'contained'} disabled={!ready} onClick={() => run(true)}>
              Change owner now
            </Button>
          )}
          {modes.queue && (
            <Button size="small" variant="contained" disabled={!ready} onClick={() => run(false)}>
              Add to the draft
            </Button>
          )}
        </Box>
      </Box>
    );
  }
  return ChangeOwnerWidget;
}
